import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

const BASE_URL = (() => {
  if (typeof window === 'undefined') {
    if (import.meta.env.DEV || Boolean(process.env.IS_OFFLINE ?? false)) {
      return 'http://localhost:3000/api/widget/v1/'
    }
    return (process.env.API_URL ?? '/api/widget/v1/')
  } else {
    const link: HTMLLinkElement | null = document.querySelector('link[rel="https://api.w.org/"]')
    if (link != null) {
      return `${link.href}gen25/booking-module/v1/`
    }
  }
  return '/api/widget/v1/'
})()

async function fetchResource<T extends z.Schema> (path: string, schema: T): Promise<z.infer<T>> {
  const url = `${BASE_URL}${path}`
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`)
  }
  const body = await response.json()
  return schema.parse(body)
}

const cabinSchema = z.object({
  available_count: z.number(),
  on_request_count: z.number(),
  total_count: z.number(),
  unit_price: z.number().transform((price) => price / 100),
  surcharge_price: z.number().nullable().transform((price) => price == null ? price : price / 100),
  discount_price: z.number().nullable().transform((price) => price == null ? price : price / 100),
  cabin_type_id: z.string(),
  cabin_type_external_id: z.string(),
  cabin_type_base_id: z.string(),
  cabin_type_base_external_id: z.string(),
  minimum_occupancy: z.number(),
  maximum_occupancy: z.number(),
  name: z.string()
})

const discountSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('percentage'), percentage: z.number() }),
  z.object({ type: z.literal('amount'), amount: z.number().transform(value => value / 100.0) })
])

const departureSchema = z.object({
  id: z.string(),
  start_date: z.iso.date(),
  start_year: z.number(),
  end_date: z.iso.date(),
  sgr: z.number().transform(value => value / 100.0),
  tour: z.object({ id: z.string(), name: z.string(), guidance: z.array(z.string()).nullable() }),
  ship: z.object({ id: z.string(), name: z.string() }),
  port_of_departure: z.object({ id: z.string(), name: z.string() }),
  status: z.enum(['available', 'fully_booked', 'on_request']),
  cabins: z.array(cabinSchema),
  discount: discountSchema.nullable()
})

const harbourSchema = z.object({
  id: z.string(),
  name: z.string(),
  translations: z.array(z.object({
    name: z.string(),
    language: z.string()
  }))
})

const shipTranslation = z.object({
  name: z.string(),
  language: z.string(),
  image_urls: z.array(z.string()),
  description: z.string().nullable()
})

const cabinTypeTranslation = z.object({
  parent_id: z.string(),
  ship_id: z.string(),
  name: z.string(),
  language: z.string(),
  image_urls: z.array(z.string()),
  description: z.string().nullable()
})

const productTranslation = z.object({
  parent_id: z.string(),
  name: z.string(),
  language: z.string(),
  description: z.string().nullable()
})

const shipSchema = z.object({
  id: z.string(),
  name: z.string(),
  translations: z.array(shipTranslation)
})

export type Cabin = z.infer<typeof cabinSchema>
export type Departure = z.infer<typeof departureSchema>
export type Discount = z.infer<typeof discountSchema>
export type ShipTranslation = z.infer<typeof shipTranslation>
export type CabinTypeTranslation = z.infer<typeof cabinTypeTranslation>

const widgetResponseSchema = z.object({
  departures: z.array(departureSchema),
  translations: z.object({
    harbours: z.array(harbourSchema),
    ships: z.array(shipSchema),
    cabin_types: z.array(cabinTypeTranslation),
    sgr: z.array(productTranslation)
  }),
  cursor: z.string().nullable()
})

export type WidgetResponse = z.infer<typeof widgetResponseSchema>
export type WidgetTranslations = WidgetResponse['translations']

async function fetchDepartures ({ tourId, shipId, year }: {
  tourId?: string
  shipId?: string
  year?: number
}): Promise<WidgetResponse> {
  const urlParams = new URLSearchParams()
  if (tourId != null) {
    urlParams.set('tour', tourId)
  }
  if (shipId != null) {
    urlParams.set('ship', shipId)
  }
  if (year != null) {
    urlParams.set('year', year.toString(10))
  }
  return await fetchResource(`departures?${urlParams.toString()}`, widgetResponseSchema)
}

type OptionalYearFilter<T extends readonly unknown[]> =
  T | readonly [...T, 'year', number]
type OptionalShipFilter<T extends readonly unknown[]> =
  T | readonly [...T, 'ship', string]
type OptionalTourFilter<T extends readonly unknown[]> =
  T | readonly [...T, 'tour', string]

type DepartureQueryKey = OptionalYearFilter<OptionalShipFilter<OptionalTourFilter<readonly ['departures']>>>

function departureQueryKey ({ tourId, shipId, year }: {
  tourId?: string
  shipId?: string
  year?: number
}): DepartureQueryKey {
  const base = ['departures'] as const
  const tour = tourId != null ? [...base, 'tour', tourId] as const : base
  const ship = shipId != null ? [...tour, 'ship', shipId] as const : tour
  return year != null ? [...ship, 'year', year] as const : ship
}

interface DepartureQueryParams {
  tourId?: string
  shipId?: string
  year?: number
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred from queryOptions overloads
export const departureQueryOptions = ({ tourId, shipId, year }: DepartureQueryParams) => queryOptions({
  queryKey: departureQueryKey({ tourId, shipId, year }),
  staleTime: 30_000,
  queryFn: async () => await fetchDepartures({ tourId, shipId, year })
})
